import { ConnectedPosition, Overlay } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import {
  booleanAttribute,
  ComponentRef,
  computed,
  Directive,
  effect,
  ElementRef,
  inject,
  input,
  OnDestroy,
  signal,
} from '@angular/core';
import { DEFAULT_TOOLTIP_POSITIONS, TooltipComponent } from '../tooltip.component';

const HIDDEN_TEST_WAIT = 500;

@Directive({
  selector: '[glxyTooltip]',
  exportAs: 'glxyTooltip',
  host: {
    '(mouseenter)': 'onMouseEnter()',
    '(mouseleave)': 'onMouseLeave()',
  },
})
export class GalaxyTooltipDirective implements OnDestroy {
  /** The text of the tooltip */
  readonly glxyTooltip = input.required<string>();

  /** An optional title that can be given to the tooltip */
  readonly tooltipTitle = input<string>();

  /** Disables the tooltip when true */
  readonly glxyTooltipDisabled = input(false, { transform: booleanAttribute });

  /**
   * What positioning strategy to use for the tooltip. If empty, no positions are applied to the popover (it has a default position set)
   */
  readonly tooltipPositions = input<ConnectedPosition[]>([]);

  /** Change the background color and text for high contrast with white backgrounds */
  readonly highContrast = input(true, { transform: booleanAttribute });

  private readonly show = signal(false);

  private readonly positions = computed(() => {
    const positions = this.tooltipPositions();
    return positions?.length ? positions : DEFAULT_TOOLTIP_POSITIONS;
  });

  private readonly elementRef = inject(ElementRef);
  private readonly overlayRef = inject(Overlay).create({ hasBackdrop: false });
  private readonly tooltip: ComponentRef<TooltipComponent> = this.overlayRef.attach(
    new ComponentPortal(TooltipComponent),
  );

  private hiddenTestID = 0;

  constructor() {
    effect(() => {
      this.tooltip.setInput('origin', this.elementRef);
      this.tooltip.setInput('title', this.tooltipTitle() || '');
      this.tooltip.setInput('text', this.glxyTooltip());
      this.tooltip.setInput('highContrast', this.highContrast());
      this.tooltip.setInput('positions', this.positions());
      this.tooltip.setInput('show', this.show());
    });
  }

  ngOnDestroy(): void {
    this.overlayRef?.dispose();
  }

  showTooltip(): void {
    if (!this.glxyTooltipDisabled()) {
      this.show.set(true);
    }
  }

  hideTooltip(): void {
    this.show.set(false);
  }

  protected onMouseEnter(): void {
    this.showTooltip();

    // Stop any previous tests, if any
    window.clearTimeout(this.hiddenTestID);
    // Kick off a new test loop
    this.testElementHidden();
  }

  protected onMouseLeave(): void {
    this.hideTooltip();

    // Stop current test from running
    window.clearTimeout(this.hiddenTestID);
  }

  /**
   * Test to see if the parent element is in the DOM. If not, hide the tooltip
   */
  private testElementHidden(): void {
    if (!this.show()) {
      return;
    }

    this.hiddenTestID = window.setTimeout(() => {
      if (!document.body.contains(this.elementRef.nativeElement)) {
        this.hideTooltip();
      }
      this.testElementHidden();
    }, HIDDEN_TEST_WAIT);
  }
}
